import { useState, useEffect, useCallback, useRef } from "react"
import {
  useGetAgentHistoriesApi,
  useGetAgentHistoryDetailApi,
  agentHistorySummaryResponseToAgentHistory,
  agentHistoryResponseToAgentHistory,
  AgentHistorySummaryResponse,
  AgentHistoryResponse,
} from "@/hooks/api"
import { Pagination } from "@/types/network"
import { AgentHistory } from "@/types"

interface UseAgentHistoriesOptions {
  initialLimit?: number
  filterHistories?: (histories: AgentHistory[]) => AgentHistory[]
  initialVersion?: number
}

// Simple state object containing all history-related state and functions
export interface AgentHistoryDropdownState {
  // Histories list state
  histories: AgentHistory[]
  isLoadingInitial: boolean
  isLoadingMore: boolean
  hasMore: boolean
  status: string

  // Current history state
  currentHistory: AgentHistory | null
  isLoadingCurrentHistory: boolean

  // Actions
  loadMore: () => void
  updateHistoryPublishStatus: (historyId: string, wasPublished: boolean) => void
  unpublishAllHistories: () => void
  setCurrentHistoryId: (historyId: string | null) => void
  refreshCurrentHistory: () => void
  loadInitial: () => void

  // Individual error states for component-level handling
  historiesError: unknown
  currentHistoryDetailError: unknown
}

export const useAgentHistories = (
  agentId: string,
  {
    initialLimit = 10,
    filterHistories,
    initialVersion,
  }: UseAgentHistoriesOptions = {}
): AgentHistoryDropdownState => {
  const [histories, setHistories] = useState<AgentHistory[]>([])
  const [pagination, setPagination] = useState<Pagination | null>(null)
  const [isLoadingInitial, setIsLoadingInitial] = useState(false)
  const [isLoadingMore, setIsLoadingMore] = useState(false)

  // Current history state
  const [currentHistory, setCurrentHistory] = useState<AgentHistory | null>(
    null
  )
  const [currentHistoryId, setCurrentHistoryId] = useState<string | null>(null)
  const [isLoadingCurrentHistory, setIsLoadingCurrentHistory] = useState(false)

  // Track current agent to prevent stale API calls
  const currentAgentIdRef = useRef<string>(agentId)

  // API hook for fetching list
  const {
    data: historiesResponse,
    error: historiesError,
    status: historiesStatus,
    fetchFn: fetchHistories,
  } = useGetAgentHistoriesApi(agentId)

  // API hook for fetching current history details
  const {
    data: currentHistoryResponse,
    error: currentHistoryDetailError,
    fetchFn: fetchCurrentHistory,
  } = useGetAgentHistoryDetailApi(agentId, currentHistoryId || "")

  const loadInitial = useCallback(() => {
    if (!agentId) return
    setIsLoadingInitial(true)
    const params = new URLSearchParams()
    params.append("limit", initialLimit.toString())
    fetchHistories({ searchParams: params }).finally(() =>
      setIsLoadingInitial(false)
    )
    setCurrentHistoryId(null)
    setHistories([])
  }, [agentId, fetchHistories, initialLimit])

  // load more
  const loadMore = useCallback(() => {
    if (!pagination?.next_page || isLoadingMore) return
    setIsLoadingMore(true)
    const params = new URLSearchParams()
    params.append("cursor", pagination.next_page)
    params.append("limit", initialLimit.toString())
    fetchHistories({ searchParams: params }).finally(() =>
      setIsLoadingMore(false)
    )
  }, [pagination, isLoadingMore, fetchHistories, initialLimit])

  // Fetch current history details
  const refreshCurrentHistory = useCallback(() => {
    if (!currentHistoryId || !agentId) return
    // Guard against agent changes during the function execution
    if (currentAgentIdRef.current !== agentId) return
    setIsLoadingCurrentHistory(true)
    fetchCurrentHistory().finally(() => setIsLoadingCurrentHistory(false))
  }, [currentHistoryId, agentId, fetchCurrentHistory])

  // handle response
  useEffect(() => {
    // Handle error state
    if (historiesError) {
      console.error("Error fetching agent histories:", historiesError)
      return
    }

    if (!historiesResponse) return
    const response = historiesResponse.data as
      | AgentHistorySummaryResponse[]
      | undefined
    if (!response) return

    const newHistories = response.map(agentHistorySummaryResponseToAgentHistory)
    const filteredNewHistories = filterHistories
      ? filterHistories(newHistories)
      : newHistories

    setHistories((prev) => {
      if (prev.length === 0) return filteredNewHistories
      // -------------------------------------------------------------------
      // DEDUPLICATION + ORDERING LOGIC
      // -------------------------------------------------------------------
      // We can be called in two very different scenarios:
      //   1. "Apply"  – user updates the agent → API returns a *new* history
      //                  which should be PREPENDED to the front of the list.
      //   2. "Load More" – user scrolls to the bottom and we fetch OLDER
      //                  histories (cursor-based pagination) → these should
      //                  be APPENDED to the back of the list.
      //
      // Unfortunately the backend doesn't tag which case we're in, so we
      // infer it by comparing timestamps:
      //   • If the newest timestamp in the incoming batch is NEWER than the
      //     newest one we already have  → treat as scenario 1 (prepend).
      //   • Otherwise                                                   → scenario 2 (append).
      //
      // We must also protect against duplicates because networking / race
      // conditions (or the "Apply" flow triggering a second list fetch)
      // could send us the same history twice.  A simple Set dedup handles
      // that before timestamp comparison.

      // Step 1 – filter out IDs we already have
      const existingIds = new Set(prev.map((h) => h.id))
      const uniqueNew = filteredNewHistories.filter(
        (h) => !existingIds.has(h.id)
      )
      if (uniqueNew.length === 0) return prev

      // Step 2 – compare timestamps to decide prepend vs append
      const newestPrev = Math.max(
        ...prev.map((h) => new Date(h.createdAt).getTime())
      )
      const newestNew = Math.max(
        ...uniqueNew.map((h) => new Date(h.createdAt).getTime())
      )
      const isNewer = newestNew > newestPrev
      return isNewer ? [...uniqueNew, ...prev] : [...prev, ...uniqueNew]
    })

    setPagination(historiesResponse.pagination ?? null)
  }, [historiesResponse, historiesError, filterHistories])

  // Auto-set current history when histories are loaded
  useEffect(() => {
    if (histories.length > 0 && !currentHistoryId) {
      let targetHistory: AgentHistory | null = null

      if (initialVersion && initialVersion !== -1) {
        // Find the history with the specific version
        targetHistory =
          histories.find((h) => h.versionNumber === initialVersion) || null
      }

      // If no specific version found or no initialVersion provided, use the latest
      if (!targetHistory) {
        targetHistory = histories[0] // Latest history (first in the list)
      }

      if (targetHistory) {
        setCurrentHistoryId(targetHistory.id)
      }
    }
  }, [histories, currentHistoryId, initialVersion])

  // Handle current history response
  useEffect(() => {
    // Handle error state
    if (currentHistoryDetailError) {
      console.error(
        "Error fetching current history detail:",
        currentHistoryDetailError
      )
      return
    }

    const resp = currentHistoryResponse?.data
    // Only set when the response is a valid history object (has id)
    if (resp && typeof resp === "object" && "id" in resp) {
      const fullCurrentHistory = agentHistoryResponseToAgentHistory(
        resp as AgentHistoryResponse
      )
      setCurrentHistory(fullCurrentHistory)
    }
  }, [currentHistoryResponse, currentHistoryDetailError])

  // Auto fetch current history when currentHistoryId changes
  useEffect(() => {
    if (currentHistoryId && agentId) {
      // Guard against agent changes during effect execution
      if (currentAgentIdRef.current !== agentId) {
        return
      }
      // Guard against stale history ID from previous agent
      const existsInCurrentList = histories.some(
        (h) => h.id === currentHistoryId
      )
      if (!existsInCurrentList) {
        return
      }
      refreshCurrentHistory()
    } else {
      setCurrentHistory(null)
    }
  }, [currentHistoryId, agentId, refreshCurrentHistory, histories])

  // Reset state and load initial when agentId changes
  useEffect(() => {
    if (!agentId) return
    // Update the ref to track current agent
    currentAgentIdRef.current = agentId
    // Clear previous agent state to avoid leaking versions across agents
    setHistories([])
    setPagination(null)
    setCurrentHistory(null)
    setCurrentHistoryId(null)
    loadInitial()
  }, [agentId, loadInitial])

  const updateHistoryPublishStatus = useCallback(
    (historyId: string, wasPublished: boolean) => {
      setHistories((prev) =>
        prev.map((h) => (h.id === historyId ? { ...h, wasPublished } : h))
      )
    },
    []
  )

  const unpublishAllHistories = useCallback(() => {
    setHistories((prev) =>
      prev.map((h) => ({
        ...h,
        wasPublished: false,
        publishedAt: undefined,
        status: "draft" as const,
      }))
    )
  }, [])

  return {
    histories,
    isLoadingInitial,
    isLoadingMore,
    hasMore: !!pagination?.next_page,
    status: historiesStatus,
    currentHistory,
    isLoadingCurrentHistory,
    loadMore,
    updateHistoryPublishStatus,
    unpublishAllHistories,
    setCurrentHistoryId,
    refreshCurrentHistory,
    loadInitial,
    // Expose individual error states for component-level handling
    historiesError,
    currentHistoryDetailError,
  }
}
